import React, { useState } from "react";
import { useLocation, useNavigate, matchPath } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FaGraduationCap,
  FaUser,
  FaSignOutAlt,
  FaTachometerAlt,
} from "react-icons/fa";
import { logout } from "../../store/slices/auth/asyncThunks";
import { selectUser } from "../../store/slices/auth/slice";
import ConfirmModal from "../utils/ConfirmModal";
import AnimatedMenuIcon from "../utils/AnimatedMenuIcon";
import CertificateApplicationModal from "../CertificateApplicationModal/CertificateApplicationModal";
import {
  UserModalProvider,
  useUserModal,
} from "../../contexts/UserModalContext";
import { useScrollLock } from "../../hooks/useScrollLock";
import { VideoSidebarProvider } from "../../contexts/VideoSidebarContext";
import GlobalVideoSidebar from "../GlobalVideoSidebar/GlobalVideoSidebar";
import { getInitials } from "../../utils/utils";
import {
  Container,
  Header,
  HeaderContent,
  AdminLogo,
  HeaderActions,
  UserInfo,
  UserAvatar,
  UserDetails,
  LogoutBtn,
  Tooltip,
  MainLayout,
  Sidebar,
  SidebarNav,
  NavItem,
  NavLink,
  MainContent,
  UserMobileMenuOverlay,
  UserMobileMenuContainer,
  UserMobileMenuHeader,
  UserMobileUserInfo,
  UserMobileAvatar,
  UserMobileDetails,
  UserMobileNav,
  UserMobileNavLink,
  UserMobileMenuDivider,
  UserMobileActions,
  UserMobileLogoutBtn,
  SidebarTitle,
  LogoButton,
  MobileMenuButton,
} from "./UserLayout.styles";
import LogoImg from "../../assets/images/logo-01.png";

const UserLayoutContent = ({ children }) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  const { certificateApplicationModal, closeCertificateApplicationModal } =
    useUserModal();
  const [showLogoutTooltip, setShowLogoutTooltip] = useState(false);
  const [isLogoutLoading, setIsLogoutLoading] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useScrollLock(isMobileMenuOpen);
  const location = useLocation();
  const navigate = useNavigate();

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const truncatedEmail =
    user?.email?.length > 15
      ? user.email.slice(0, 15) + "..."
      : user?.email || "";
  const displayName = fullName || truncatedEmail;

  const navigationItems = [
    { path: "/kursevi", icon: FaGraduationCap, label: "Moji Kursevi" },
    { path: "/profil", icon: FaUser, label: "Profil" },
  ];

  const isItemActive = (item) =>
    location.pathname === "/korisnicki-panel" + item.path ||
    (item.path === "/kursevi" &&
      location.pathname.startsWith("/korisnicki-panel/kursevi"));
  // Removed duplicate auth check - ProtectedRoute handles this

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = async () => {
    try {
      setIsLogoutLoading(true);
      await dispatch(logout()).unwrap();
      navigate("/pristup");
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setIsLogoutLoading(false);
    }
  };

  const handleNavigation = (path) => {
    navigate(`/korisnicki-panel${path}`);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleMobileNavigation = (path) => {
    closeMobileMenu();
    setTimeout(() => {
      navigate(`/korisnicki-panel${path}`);
    }, 300);
  };

  const handleMobileLogout = () => {
    closeMobileMenu();
    setShowLogoutConfirm(true);
  };

  return (
    <Container>
      <Header>
        <HeaderContent>
          <LogoButton
            type="button"
            aria-label="Idi na početnu stranu"
            onClick={() => {
              navigate("/");
              setTimeout(() => {
                window.scrollTo(0, 0);
              }, 300);
            }}
          >
            <AdminLogo src={LogoImg} alt="MM BeautySpot" />
          </LogoButton>

          <HeaderActions>
            <UserInfo>
              <UserAvatar>{getInitials(user)}</UserAvatar>
              <UserDetails>
                <h4>{displayName}</h4>
                <p>Beauty Student</p>
              </UserDetails>
            </UserInfo>
            <LogoutBtn
              onClick={handleLogout}
              disabled={isLogoutLoading}
              aria-label="Odjavite se"
              onMouseEnter={() => setShowLogoutTooltip(true)}
              onMouseLeave={() => setShowLogoutTooltip(false)}
              onFocus={() => setShowLogoutTooltip(true)}
              onBlur={() => setShowLogoutTooltip(false)}
            >
              <FaSignOutAlt />
              <Tooltip $show={showLogoutTooltip}>
                {isLogoutLoading ? "Odjavljujemo..." : "Odjavite se"}
              </Tooltip>
            </LogoutBtn>
          </HeaderActions>

          <MobileMenuButton
            type="button"
            onClick={toggleMobileMenu}
            aria-label={isMobileMenuOpen ? "Zatvori meni" : "Otvori meni"}
            aria-expanded={isMobileMenuOpen}
          >
            <AnimatedMenuIcon
              panel
              isOpen={isMobileMenuOpen}
              color="var(--accent)"
            />
          </MobileMenuButton>
        </HeaderContent>
      </Header>

      <MainLayout>
        <Sidebar>
          <SidebarTitle>
            <FaTachometerAlt />
            Glavne Opcije
          </SidebarTitle>
          <SidebarNav>
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavItem key={item.path}>
                  <NavLink
                    type="button"
                    $active={isItemActive(item)}
                    aria-current={isItemActive(item) ? "page" : undefined}
                    onClick={() => handleNavigation(item.path)}
                  >
                    <Icon />
                    {item.label}
                  </NavLink>
                </NavItem>
              );
            })}
          </SidebarNav>
        </Sidebar>

        <MainContent
          $noPadding={
            !!matchPath(
              "/korisnicki-panel/kursevi/:courseId/lekcije/:lessonId",
              location.pathname,
            )
          }
        >
          {children}
        </MainContent>
      </MainLayout>

      <ConfirmModal
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={confirmLogout}
        title="Potvrda odjave"
        message="Da li ste sigurni da se želite odjaviti?"
        confirmText="Odjavi se"
        cancelText="Otkaži"
      />

      <UserMobileMenuOverlay
        $isOpen={isMobileMenuOpen}
        onClick={closeMobileMenu}
      />
      <UserMobileMenuContainer $isOpen={isMobileMenuOpen}>
        <UserMobileMenuHeader>
          <UserMobileUserInfo>
            <UserMobileAvatar>{getInitials(user)}</UserMobileAvatar>
            <UserMobileDetails>
              <h4>{displayName}</h4>
              <p>Beauty Student</p>
            </UserMobileDetails>
          </UserMobileUserInfo>
        </UserMobileMenuHeader>

        <UserMobileNav>
          <ul>
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.path}>
                  <UserMobileNavLink
                    type="button"
                    $active={isItemActive(item)}
                    aria-current={isItemActive(item) ? "page" : undefined}
                    onClick={() => handleMobileNavigation(item.path)}
                  >
                    <Icon />
                    {item.label}
                  </UserMobileNavLink>
                </li>
              );
            })}
          </ul>
        </UserMobileNav>

        <UserMobileMenuDivider />

        <UserMobileActions>
          <UserMobileLogoutBtn onClick={handleMobileLogout}>
            <FaSignOutAlt />
            Odjavi se
          </UserMobileLogoutBtn>
        </UserMobileActions>
      </UserMobileMenuContainer>

      {/* Certificate Application Modal */}
      <CertificateApplicationModal
        isOpen={certificateApplicationModal.isOpen}
        courseId={certificateApplicationModal.courseId}
        courseTitle={certificateApplicationModal.courseName}
        onClose={closeCertificateApplicationModal}
      />

      {/* Global Video Sidebar */}
      <GlobalVideoSidebar />
    </Container>
  );
};

const UserLayout = ({ children }) => {
  return (
    <UserModalProvider>
      <VideoSidebarProvider>
        <UserLayoutContent>{children}</UserLayoutContent>
      </VideoSidebarProvider>
    </UserModalProvider>
  );
};

export default UserLayout;
